/**
 * The base of every checked-request error the SDK throws, in either package.
 *
 * `SdkRequestError` lives here, in `@tento/lona/sheets`, so the rows
 * package's own boundary (`RowsHttpBackendRequestError`) and every `@tento/lona`
 * boundary (`TaskWriteError`, `EventWriteError`, `RowWriteError`,
 * `CellWriteError`, `SheetWriteError`, `SheetReadError`, `SheetOpenError`,
 * `PromoteWriteError`) share ONE class: a single
 * `e instanceof SdkRequestError` covers a refused or unanswered request from
 * anywhere in the SDK. `@tento/lona` re-exports it unchanged.
 *
 * `RowWriteError` lives here too, because this package's own row writes
 * (`Sheet.setRendererDisplayAttr`) throw it; `@tento/lona` re-exports it.
 *
 * The step lexicon (which `step` names which request) is tabled in
 * `@tento/lona`'s `errors.ts`.
 */

import type { Option } from "./option.ts";
import { Result } from "@tento-core/result";
import { Tracing } from "@tento-core/tracing";
import { RequestTransportError, ResponseDecodeError, type ResponseError } from "@tento-core/typed-api";
import { WireDecodeError } from "../wire/wire-decode.ts";

/**
 * What a request's rejection stands for. `TypedApi` marks a rejection of the
 * transport promise itself as a `RequestTransportError`; its `cause` is what
 * the transport threw, which is the request's evidence: the platform's own
 * failure (a `TypeError`), or an already typed `SdkRequestError` a host
 * transport refused with. Anything else is itself. Every SDK classifier
 * reads a rejection through this, so a typed refusal keeps its verdict and a
 * raw failure stays the `cause`.
 */
export function requestRejection(err: unknown): unknown {
  return err instanceof RequestTransportError ? err.cause : err;
}

/** HTTP verb of a checked SDK request. */
export type SdkRequestMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

/**
 * The SDK boundary that sent a checked request (`SdkRequestError.boundary`).
 * Each subclass narrows it to its own (`TaskWriteError` → `"tasks"`,
 * `RowsHttpBackendRequestError` → `"rows-http"`, …); the base class carries the
 * boundaries with no dedicated subclass (`"user"`, `"calendars"`, `"sync"`,
 * `"scheduling-links"`, the CLI's `"cli"`) and `"request"`, the tolerant
 * request a `RequestErrorHandler` receives.
 */
export type SdkRequestBoundary =
  | "tasks"
  | "events"
  | "rows"
  | "cells"
  | "sheets"
  | "promote"
  | "rows-http"
  | "request"
  | "user"
  | "calendars"
  | "sync"
  | "scheduling-links"
  | "cli";

/**
 * What became of a checked request (`SdkRequestError.outcome`) — the fact a
 * caller needs before retrying:
 * - `"refused"`: the server answered with a non-2xx `status`. Nothing was
 *   stored; a retry is a new request.
 * - `"unanswered"`: the request was sent and no verdict came back (a
 *   transport failure, a service-worker reply that timed out). The server
 *   may have stored it; a retry may duplicate it.
 * - `"malformed"`: the server answered 2xx but its body could not be read
 *   (not JSON, or a value its wire type rejects — `ResponseDecodeError`,
 *   `WireDecodeError`). The server may have stored it; the SDK cannot tell
 *   what it answered, so nothing reads it as a miss or an empty result. A
 *   followed redirect to a non-JSON page (a login page, say) is malformed
 *   too: it is not the route's answer, so a checked write it answered rolls
 *   back rather than confirming what the server may never have stored.
 * - `"unaddressable"`: the request was never sent — the SDK could not
 *   address it (no writable source row, an unresolvable master event). A
 *   retry is safe once the cause is fixed.
 * - `"faulted"`: the SDK's own code failed while handling the request (a
 *   bug, a broken invariant) — not the transport and not the server. It
 *   says nothing of the request: it may never have been sent, or been
 *   answered. A retry runs the same code; report it as a fault, never as
 *   lost connectivity.
 *
 * `status` is non-null exactly when the outcome is `"refused"`.
 */
export type SdkRequestOutcome = "refused" | "unanswered" | "malformed" | "unaddressable" | "faulted";

/**
 * The outcome of a failed request with the status it carries: a `"refused"`
 * request has the server's HTTP `status`; an `"unanswered"`, `"malformed"`,
 * `"unaddressable"` or `"faulted"` one has none. What `SdkRequestError`'s constructor
 * takes, so a status can only be given with a refusal.
 */
export type SdkRequestFailure =
  | { readonly outcome: "refused"; readonly status: number }
  | { readonly outcome: "unanswered" }
  | { readonly outcome: "malformed" }
  | { readonly outcome: "unaddressable" }
  | { readonly outcome: "faulted" };

/**
 * The server's category slug for a refused request (`ResponseError.reason`).
 * The slugs the SDK itself sets or branches on are named; any other string
 * the server sends is kept as is (the `string & {}` member), so a new server
 * slug never fails to type-check. `SdkRequestError.reason` is `null` when
 * the server gave none.
 */
export type SdkRequestReason =
  | "not_found"
  | "cross_owner"
  | "lookup_key_conflict"
  // A create carried a client-minted row id another owner's row holds
  // (409; server row-identity stage 1). The SDK mints a new id for a retry.
  | "row_id_conflict"
  | "calendar_gone"
  | "not_logged_in"
  // A 429: the per-user rate limit (`SyncPollThrottledError`, `BatchFetchThrottledError`).
  | "rate_limited"
  // A refused calendar event write, from the host's provider mapping: the
  // account's token (or the calendar) cannot write; the write raced a newer
  // provider version; the event belongs to another organizer (Google's 403
  // `forbiddenForNonOrganizer`). Same `{ reason, details }` body as the rest.
  | "calendar_read_only"
  | "calendar_write_conflict"
  | "calendar_not_organizer"
  // Any other server slug: `string & {}` keeps the named ones suggested.
  | (string & {});

/**
 * A required SDK request that did not succeed: the server refused it (a
 * non-2xx answer), never answered it (transport failure), it could not be
 * sent at all, or the SDK's own code failed handling it — `outcome` says
 * which. The base of every boundary error.
 *
 * The SDK's required requests go through the result-returning `TypedApi`
 * forms (`getResult` / `postResult` / `patchResult` / `delResult`), never the
 * tolerant `get` / `post` / `patch` / `del`, which hand a non-2xx to the
 * request-error delegate and RESOLVE with `undefined` — awaiting those proves
 * nothing. By the time one of these is thrown the SDK has already rolled back
 * whatever it applied optimistically, so the caller only has to report it.
 *
 * `message` names the boundary, the step, the request, and the server's
 * `{ reason, details }`; the same facts are typed fields for callers that
 * branch on them (`status === 404`, `reason === "calendar_gone"`).
 *
 * `F` is the failure a subclass can carry (`RowsHttpBackendRequestError` is
 * `SdkRequestError<SdkRequestError.Answered>`: refused or unanswered, never
 * unaddressable); `outcome` and `status` read it.
 */
export class SdkRequestError<F extends SdkRequestFailure = SdkRequestFailure> extends Error {
  /** The SDK boundary that sent the request (`"tasks"`, `"rows"`, `"rows-http"`, …). */
  readonly boundary: SdkRequestBoundary;
  /** The SDK operation that failed (`"update"`, `"delete"`, …). */
  readonly step: string;
  readonly method: SdkRequestMethod;
  /** The route template, e.g. `/api/tasks/{taskId}`. */
  readonly path: string;
  /**
   * What became of the request, with the status of a refusal
   * (`SdkRequestFailure`) — the one record `outcome` and `status` read.
   */
  readonly failure: F;
  /** The server's category slug (`ResponseError.reason`); `null` when it gave none. */
  readonly reason: Option<SdkRequestReason>;
  /** The server's sentence, or the transport error's message; `""` when absent. */
  readonly details: string;

  /**
   * A `failure` whose status does not match its outcome (a `"refused"` one
   * without an integer `status`, any other with one) cannot be written in
   * TypeScript; from untyped code it is a bug at the construction site and
   * throws.
   */
  constructor(init: SdkRequestError.Init<NoInfer<F>>) {
    const { boundary, step, method, path, failure } = init;
    if (
      failure.outcome === "refused"
        ? !Number.isInteger(failure.status)
        : "status" in failure
    ) {
      throw new Error(
        `SdkRequestError: outcome ${failure.outcome} with status ` +
          `${String(Reflect.get(failure, "status"))} (${boundary} ${step} ${method} ${path})`,
      );
    }
    const reason = init.reason == null || init.reason === "" ? null : init.reason;
    const details = init.details ?? "";
    const outcome = failure.outcome;
    const status = failure.outcome === "refused" ? failure.status : null;
    const server = [reason ?? "", details].filter((part) => part.length > 0)
      .join(": ");
    super(
      `${boundary} ${step} failed: ${method} ${path}` +
        (status != null ? ` -> ${status}` : ` (${outcome})`) +
        `: ${server.length > 0 ? server : SdkRequestError.#silence(outcome)}`,
    );
    this.name = "SdkRequestError";
    this.boundary = boundary;
    this.step = step;
    this.method = method;
    this.path = path;
    this.failure = failure;
    this.reason = reason;
    this.details = details;
    Tracing.log("sdk-request-error", {
      boundary,
      step,
      method,
      path,
      outcome,
      status,
      reason,
      details,
    });
  }

  /** Whether the server refused the request, never answered it, it was
   *  never sent, or the SDK faulted handling it (`failure.outcome`). */
  get outcome(): F["outcome"] {
    return this.failure.outcome;
  }

  /** HTTP status of a `"refused"` request; `null` for every other outcome. */
  get status(): Option<number> {
    return this.failure.outcome === "refused" ? this.failure.status : null;
  }

  /** The message tail when the server said nothing. */
  static #silence(outcome: SdkRequestOutcome): string {
    switch (outcome) {
      case "refused":
        return "no reason given";
      case "unanswered":
        return "no response";
      case "malformed":
        return "the response could not be read";
      case "unaddressable":
        return "not sent";
      case "faulted":
        return "the SDK failed handling it";
    }
  }

  /**
   * Whether `err` is a failed request the server may nonetheless have
   * stored: an `SdkRequestError` whose outcome is `"unanswered"` (no verdict
   * came back) or `"malformed"` (a 2xx the SDK could not read). A refusal
   * (nothing stored), a request never sent (`"unaddressable"`), a fault in
   * the SDK's own code (`"faulted"`: a bug, reported as one) and anything
   * that is not an `SdkRequestError` answer `false`. The SDK's checked
   * writes take such a write back and then re-read the server's state, as
   * their durable twins do, so a write that did land comes back; a caller
   * retrying one checks it first (a retry may duplicate it).
   *
   * ```ts
   * catch (err) { if (SdkRequestError.mayHaveStored(err)) showMaybeSaved(); }
   * ```
   */
  static mayHaveStored(err: unknown): err is SdkRequestError {
    return err instanceof SdkRequestError && (err.outcome === "unanswered" || err.outcome === "malformed");
  }

  /**
   * The outcome of a request whose only evidence is an HTTP-ish status: a
   * status (other than `0`, a fetch that never reached the server) is a
   * refusal; `null` or `0` is no verdict.
   */
  static answered(status: Option<number>): SdkRequestError.Answered {
    return status == null || status === 0
      ? { outcome: "unanswered" }
      : { outcome: "refused", status };
  }

  /**
   * The outcome of a request that threw instead of answering with a
   * `Result`, read through `requestRejection`: a typed refusal a host
   * transport threw keeps its own (one that was never answered — not sent,
   * or an SDK fault — reads `"unanswered"`); a body that did not decode
   * (`ResponseDecodeError`, `WireDecodeError`) is `"malformed"` — the server
   * answered; anything else (a transport failure, a timed-out service-worker
   * reply) is `"unanswered"`.
   */
  static thrown(err: unknown): SdkRequestError.Answered {
    const raw = requestRejection(err);
    if (raw instanceof SdkRequestError) {
      const failure: SdkRequestFailure = raw.failure;
      if (failure.outcome === "refused" || failure.outcome === "malformed") return failure;
      return { outcome: "unanswered" };
    }
    return raw instanceof ResponseDecodeError || raw instanceof WireDecodeError
      ? { outcome: "malformed" }
      : { outcome: "unanswered" };
  }

  /**
   * The error `make` builds for a request that threw instead of answering
   * with a `Result`: its failure is `thrown(err)`; a typed refusal's reason
   * and words carry over, any other rejection's message is `details`; and
   * the rejection (`requestRejection`: what the transport threw) is its
   * `cause`. Every boundary that wraps a thrown request in its own error
   * builds it here: client open, promote, the rows-http backend's reads and
   * the row write wraps (`RowWriteError.after`, `RowWrites.wrap`), which keep
   * an inner `"unaddressable"` or `"faulted"` verdict of the SDK's own (that
   * write never reached the server). `sendChecked` rethrows a typed refusal
   * instead.
   */
  static rejected<E extends SdkRequestError<SdkRequestFailure>>(
    err: unknown,
    make: (answer: SdkRequestError.Answer) => E,
  ): E {
    const raw = requestRejection(err);
    const typed = raw instanceof SdkRequestError ? raw : null;
    const error = make({
      failure: SdkRequestError.thrown(raw),
      reason: typed?.reason ?? null,
      details: typed?.details ?? (raw instanceof Error ? raw.message : String(raw)),
    });
    error.cause = raw;
    return error;
  }

  /**
   * Send one required request of a boundary that has no dedicated subclass
   * (the signed-in user, the calendar list); throw `SdkRequestError` on a
   * non-2xx answer or no answer. Subclasses expose the same guard as their
   * own `check(step, …)`.
   */
  static checkRequest<T>(
    boundary: SdkRequestBoundary,
    step: string,
    method: SdkRequestMethod,
    path: string,
    send: () => Promise<Result<T, ResponseError>>,
  ): Promise<T> {
    return SdkRequestError.sendChecked(send, (answer) =>
      new SdkRequestError({ boundary, step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }

  /**
   * Send one required request and unwrap it: `Ok` resolves with the body; an
   * `Err` answer becomes the error `make` builds (`"refused"` with the
   * server's status, or `"unanswered"` for status `0`, a fetch that never
   * reached the server), a 2xx body that did not decode becomes a
   * `"malformed"` one, and any other thrown transport failure an
   * `"unanswered"` one — each carrying the thrown message. The primitive
   * under every subclass's `check`. An already typed request error passes
   * through unchanged; a raw transport or decode failure remains its cause.
   */
  static async sendChecked<T, E extends SdkRequestError<SdkRequestFailure>>(
    send: () => Promise<Result<T, ResponseError>>,
    make: (answer: SdkRequestError.Answer) => E,
  ): Promise<T> {
    let result: Result<T, ResponseError>;
    try {
      result = await send();
    } catch (thrown) {
      const err = requestRejection(thrown);
      if (err instanceof SdkRequestError) throw err;
      throw SdkRequestError.rejected(err, make);
    }
    return result.unwrapOrElse((e) => {
      throw make({
        failure: SdkRequestError.answered(e.code),
        reason: e.reason === "" ? null : e.reason,
        details: e.details,
      });
    });
  }

  /**
   * Wrap a lookup whose 404 means "absent": a 404 answer resolves
   * `Ok(null)`; every other answer passes through, so a `check` around it
   * still throws on any other failure.
   */
  static async notFoundAsNull<T>(
    send: () => Promise<Result<T, ResponseError>>,
  ): Promise<Result<Option<T>, ResponseError>> {
    const result = await send();
    const error = result.err();
    if (error != null && error.code === 404) return Result.ok(null);
    return result;
  }
}

/**
 * Types that go with `SdkRequestError`: `Init`, `SubclassInit`, `Answered`,
 * `Refused`, `Answer`.
 */
export namespace SdkRequestError {
  /**
   * What the constructor takes. An absent or empty `reason` is `null` on the
   * error; `details` defaults to `""`.
   */
  export type Init<F extends SdkRequestFailure = SdkRequestFailure> = {
    readonly boundary: SdkRequestBoundary;
    readonly step: string;
    readonly method: SdkRequestMethod;
    readonly path: string;
    readonly failure: F;
    readonly reason?: SdkRequestReason;
    readonly details?: string;
  };

  /**
   * A subclass's constructor input: the base's `Init` without the
   * `boundary` the subclass fixes, its `step` and `method` narrowed.
   */
  export type SubclassInit<
    Step extends string,
    Method extends SdkRequestMethod = SdkRequestMethod,
    F extends SdkRequestFailure = SdkRequestFailure,
  > =
    & Omit<Init<F>, "boundary" | "step" | "method">
    & { readonly step: Step; readonly method: Method };

  /** A request that was sent: refused with a status, unanswered, or
   *  answered with a body that did not decode. */
  export type Answered = Extract<
    SdkRequestFailure,
    { outcome: "refused" | "unanswered" | "malformed" }
  >;

  /** A request the server refused, with its status. */
  export type Refused = Extract<SdkRequestFailure, { outcome: "refused" }>;

  /** What `sendChecked` hands its `make`: the answer and the server's words. */
  export type Answer = {
    readonly failure: Answered;
    /** The server's category slug; `null` when it gave none. */
    readonly reason: Option<SdkRequestReason>;
    readonly details: string;
  };

  export namespace Answer {
    /**
     * `answer` as constructor fields (`Init`), a `null` reason left out: what
     * a `make` spreads into its error (`new RowWriteError({ step, method,
     * path, ...Answer.fields(answer) })`).
     */
    export function fields(answer: Answer): Pick<Init<Answered>, "failure" | "reason" | "details"> {
      return {
        failure: answer.failure,
        ...(answer.reason != null ? { reason: answer.reason } : {}),
        details: answer.details,
      };
    }
  }
}

/**
 * Thrown by `RowAccessor.add` / `delete` / `move` when the sheet-row write
 * (`POST /api/sheets/{sheetKey}/row`, `DELETE …/row/{rowKey}`,
 * `POST …/row/{rowKey}/reorder`) is rejected or unanswered, and by the
 * delete's undo action (step `"restore"`, `POST /api/rows/{rowKey}/restore`).
 * `add` and `delete` have changed nothing locally; `move` has put the row
 * back where it was.
 *
 * Also thrown by `SheetRow.Handle.update` / `SheetRow.Joined.Handle.update`
 * (step `"update"`, `PATCH /api/rows/{rowKey}`; the local apply is rolled
 * back) and is the rejection of their `updateDurable(...).settled`,
 * `mergeAttributes` / `setLookupKey` (step `"update"`), `RowsClient.linkRow`
 * (step `"link"`, `POST /api/rows/linkable`), `RowAccessor.hydrateChildren`
 * (step `"hydrate-children"`), the SDK's other `/api/rows` writes, and
 * `SyncHttpBackend`'s row writes. `RowAccessor.move` sends the reorder
 * request, so its step is `"reorder"`, as `RowApi.reorder`'s and a promote's
 * (`PromoteWriteError`) are: one step per request.
 *
 * In this package, the rejection of `Sheet.setRendererDisplayAttr` (and of the
 * `*-attr` option writes routed through it) when a row write it sends is
 * refused or unanswered: step `"add"` when the renderer row's create failed,
 * `"update"` when an attribute write failed. The attributes are back as they
 * were before the write, and `details` says which of its writes had reached
 * the server (`RowWriteError.after`).
 */
export class RowWriteError extends SdkRequestError {
  declare readonly boundary: "rows";
  declare readonly step: RowWriteError.Step;

  constructor(init: SdkRequestError.SubclassInit<RowWriteError.Step>) {
    super({ ...init, boundary: "rows" });
    this.name = "RowWriteError";
  }

  /**
   * The `RowWriteError` of a row write `err` rejected, built as every thrown
   * request's error is (`SdkRequestError.rejected`): a typed refusal's
   * outcome, status and reason carry over, a body that did not decode is
   * `"malformed"`, any other throw is `"unanswered"`. An inner write the SDK
   * never sent or faulted on (`"unaddressable"`, `"faulted"`) keeps that
   * verdict: nothing reached the server, so it never "may have stored".
   * `context` leads `details` (what the write was, and what of it reached
   * the server), followed by the failure's own words; what the transport
   * threw is the `cause`.
   */
  static after(
    err: unknown,
    step: RowWriteError.Step,
    method: SdkRequestMethod,
    path: string,
    context: string,
  ): RowWriteError {
    const inner = requestRejection(err);
    const unsent = inner instanceof SdkRequestError && (inner.outcome === "unaddressable" || inner.outcome === "faulted")
      ? inner.failure
      : null;
    return SdkRequestError.rejected(err, (answer) =>
      new RowWriteError({
        step,
        method,
        path,
        ...SdkRequestError.Answer.fields(answer),
        ...(unsent != null ? { failure: unsent } : {}),
        details: context.length === 0
          ? answer.details
          : answer.details.length > 0
          ? `${context}: ${answer.details}`
          : context,
      }));
  }

  /** Send a required row write; throw `RowWriteError` on failure. */
  static check<T>(
    step: RowWriteError.Step,
    method: SdkRequestMethod,
    path: string,
    send: () => Promise<Result<T, ResponseError>>,
  ): Promise<T> {
    return SdkRequestError.sendChecked(send, (answer) =>
      new RowWriteError({ step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }
}

/** Types that go with `RowWriteError`: `Step`. */
export namespace RowWriteError {
  export type Step =
    | "add"
    | "delete"
    | "reorder"
    | "restore"
    | "update"
    | "link"
    | "hydrate-children";
}
